import { findTerminatorInBuffer, ThreadSafeBuffer } from "./buffer";
import { TimeoutError } from "./errors";

export type LightHttpSerialPortConfig = {
  baseUrl: string;
  baudRate?: number | undefined;
  pid?: string | undefined;
  portName?: string | undefined;
  terminator?: Uint8Array;
  vid?: string | undefined;
};

export class LightSerialPort {
  private readonly _baseUrl: string;
  private readonly _baudRate: number;
  private readonly _pid: string | null = null;
  private readonly _portName: string | null = null;
  private readonly _terminatorBytes: Uint8Array;
  private _vid: string | null = null;

  private portIdPromise: Promise<string> | null = null;
  private openPromise: Promise<string> | null = null;
  private reader: ReadableStreamDefaultReader<Uint8Array> | null = null;
  private readonly buffer = new ThreadSafeBuffer();
  private pumpRunning: boolean = false;
  private pumpPromise: Promise<void> | null = null;


  constructor(config: LightHttpSerialPortConfig) {
    this._baudRate = config.baudRate ?? 9600;
    this._baseUrl = config.baseUrl.replace(/\/$/, '');
    if (this._baseUrl.length === 0) {
      throw new Error('baseUrl cannot be empty');
    }
    this._terminatorBytes = config.terminator
      ? Uint8Array.from(config.terminator)
      : new Uint8Array([0x0d, 0x0a]);
    if (this._terminatorBytes.length === 0) {
      throw new Error('Terminator cannot be empty');
    }
  }

  private async doOpen(): Promise<string> {
    const id = await this.getPortId();
    const response = await fetch(`${this._baseUrl}/api/v1/serial/open/${id}`, {
      method: 'POST',
      body: JSON.stringify({
        baudRate: this._baudRate
      }),
      headers: {
        "Content-type": "application/json; charset=UTF-8"
      }
    });

    if (!response.ok) {
      throw new Error(`Unable to open serial port: ${response.status} ${response.statusText}`);
    }

    if (response.body == null) {
      throw new Error('Unable to open serial port: response body is empty');
    }

    this.reader = response.body.getReader();
    this.startPump();
    return id;
  }


  private startPump(): void {
    this.pumpRunning = true;
    this.pumpPromise = this.pump();
  }

  private stopPump(): void {
    this.pumpRunning = false;
  }

  private async pump(): Promise<void> {
    while (this.pumpRunning && this.reader) {
      try {
        const { value, done } = await this.reader.read();
        if (done) {
          this.pumpRunning = false;
          break;
        }
        if (value) {
          console.debug('raw:', new TextDecoder().decode(value));
          await this.buffer.append(value);
        }
      } catch {
        if (this.pumpRunning) {
          this.pumpRunning = false;
        }
        break;
      }
    }
  }

  private async getPortId(): Promise<string> {
    if (this.portIdPromise == null) {
      const urlSearchParams = new URLSearchParams();
      if (this._pid) {
        urlSearchParams.append('pid', this._pid);
      }
      if (this._portName) {
        urlSearchParams.append('portName', this._portName);
      }
      if (this._vid) {
        urlSearchParams.append('vid', this._vid);
      }
      const path = `/api/v1/serial/requestPort?${urlSearchParams.toString()}`
      this.portIdPromise = fetch(`${this._baseUrl}${path}`).then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to request serial port: ${response.status} ${response.statusText}`);
        }
        return response.text();
      });
    }
    return this.portIdPromise;
  }

  async open(): Promise<string> {
    if (this.openPromise) {
      return this.openPromise;
    }
    this.openPromise = this.doOpen();
    try {
      return await this.openPromise;
    } catch (e) {
      this.openPromise = null;
      throw e;
    }
  }


  async close(): Promise<void> {
    const id = await this.getPortId();
    const response = await fetch(`${this._baseUrl}/api/v1/serial/close/${id}`, {
      method: 'POST',
    });

    if (!response.ok) {
      throw new Error(`Unable to close serial port: ${response.status} ${response.statusText}`);
    }

    this.stopPump();
    await this.pumpPromise;

    if (this.reader) {
      const reader = this.reader;
      this.reader = null;
      try {
        await reader.cancel();
      } catch {
        // Stream can already be closed by the server.
      }
      reader.releaseLock();
    }

    await this.buffer.clear();
    this.openPromise = null;
  }

  async write(data: Uint8Array<ArrayBuffer>): Promise<void> {
    const id = await this.getPortId();
    const response = await fetch(`${this._baseUrl}/api/v1/serial/write/${id}`, {
      method: 'POST',
      body: data,
      headers: {
        'Content-type': 'application/octet-stream',
      },
    });

    if (!response.ok) {
      throw new Error(`Unable to write serial data: ${response.status} ${response.statusText}`);
    }
  }

  async read(timeout: number, abortController: AbortController): Promise<Uint8Array> {
    const start = performance.now();

    while (true) {
      const result = await this.buffer.get((data) => {
        const index = findTerminatorInBuffer(data, this._terminatorBytes);
        if (index === -1) {
          return null;
        }
        console.debug('line:', new TextDecoder().decode(new Uint8Array(data.slice(0, index))));
        return {
          result: new Uint8Array(data.slice(0, index)),
          consumed: index + this._terminatorBytes.length,
        };
      });
      if (result !== null) {
        return result;
      }

      const remaining = timeout - (performance.now() - start);
      if (remaining <= 0) {
        throw new TimeoutError('Read timed out');
      }

      await this.buffer.waitForData(remaining, abortController);
    }
  }
}
